import { MouseEventHandler, useState } from "react";
import { FormattedMessage } from "react-intl";
import {
  ConnectionHandler,
  graphql,
  useFragment,
  useMutation,
} from "react-relay";
import {
  generatePath,
  useMatch,
  useNavigate,
  useRevalidator,
} from "react-router";
import { Button, ButtonProps } from "@/kit/ui/button";
import {
  ResponsiveModal,
  ResponsiveModalClose,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalPortal,
  ResponsiveModalTitle,
  ResponsiveModalTrigger,
} from "@/kit/ui/responsive-modal";
import { logger } from "@/common/logger";
import { toast } from "@/utils/toast";
import { relayErrorMessage } from "@/utils/relay";
import { WorkspaceVersionDeleteButtonFragment$key } from "./__generated__/WorkspaceVersionDeleteButtonFragment.graphql";
import { WorkspaceVersionDeleteButtonMutation } from "./__generated__/WorkspaceVersionDeleteButtonMutation.graphql";
import { Trash01Icon } from "@/icons";
import { splitWorkspaceSlug } from "@/utils/routing";

const Fragment = graphql`
  fragment WorkspaceVersionDeleteButtonFragment on WorkspaceVersion {
    id
    version
    workspace {
      id
      slug
    }
  }
`;

const Mutation = graphql`
  mutation WorkspaceVersionDeleteButtonMutation(
    $id: ID!
    $connections: [ID!]!
  ) {
    deleteWorkspaceVersion(id: $id) @deleteEdge(connections: $connections)
  }
`;

export interface WorkspaceVersionDeleteButtonProps extends ButtonProps {
  version: WorkspaceVersionDeleteButtonFragment$key;
  hasCurrentFocus?: boolean | undefined;
}

export default function WorkspaceVersionDeleteButton({
  version: versionFragment,
  hasCurrentFocus,
  ...props
}: WorkspaceVersionDeleteButtonProps) {
  const navigate = useNavigate();
  const { revalidate } = useRevalidator();
  const workspacePage = useMatch("/:owner/:slug");
  const workspaceVersionPage = useMatch("/:owner/:slug/versions/:version");
  const [isOpen, setOpen] = useState(false);
  const version = useFragment(Fragment, versionFragment);
  const [commitMutation, isMutationInFlight] =
    useMutation<WorkspaceVersionDeleteButtonMutation>(Mutation);

  const onConfirm: MouseEventHandler = () =>
    commitMutation({
      variables: {
        id: version.id,
        connections: [
          ConnectionHandler.getConnectionID(
            version.workspace.id,
            "WorkspaceVersionHistoryPaginationFragment_versions",
          ),
          ConnectionHandler.getConnectionID(
            version.workspace.id,
            "WorkspaceAsideFragment_versions",
          ),
          ConnectionHandler.getConnectionID(
            version.workspace.id,
            "WorkspaceScaffoldWorkspaceFragment_latest",
          ),
        ],
      },
      onCompleted: () => {
        setOpen(false);
        toast.success(
          <FormattedMessage
            defaultMessage="Workspace version “{version}” has successfully been deleted."
            values={{ version: version.version }}
          />,
        );

        if (hasCurrentFocus) {
          if (workspaceVersionPage) {
            navigate(
              generatePath(
                "/workspaces/:owner/:slug",
                splitWorkspaceSlug(version.workspace.slug),
              ),
              { replace: true },
            );
          } else if (workspacePage) {
            revalidate();
          }
        }
      },
      onError: (error) => {
        logger.error(error);
        toast.error(relayErrorMessage(error));
      },
    });

  const onOpenChange = (open: boolean) => {
    if (isMutationInFlight) return;
    setOpen(open);
  };

  return (
    <ResponsiveModal open={isOpen} onOpenChange={onOpenChange}>
      <ResponsiveModalTrigger asChild>
        <Button {...props} />
      </ResponsiveModalTrigger>

      <ResponsiveModalPortal>
        <ResponsiveModalContent
          className="sm:w-96 w-auto bg-linear-to-b from-[#FCEDED] from-[-16.39%] to-white to-[65.62%]"
          noClose={isMutationInFlight}
        >
          <div className="flex flex-col items-center justify-center gap-3">
            <div className="bg-error-100 rounded-md p-2 w-fit">
              <Trash01Icon className="text-error-500 w-8 h-8" />
            </div>
            <ResponsiveModalTitle>
              <FormattedMessage defaultMessage="Delete workspace version" />
            </ResponsiveModalTitle>
            <ResponsiveModalDescription className="text-center w-10/12">
              <FormattedMessage
                defaultMessage="You're about to delete “{version}” and all its data. This action can’t be undone."
                values={{ version: version.version }}
              />
            </ResponsiveModalDescription>
            <div className="w-full flex flex-row gap-2 mt-4">
              <ResponsiveModalClose asChild>
                <Button
                  variant="secondary"
                  disabled={isMutationInFlight}
                  className="w-full"
                >
                  <FormattedMessage defaultMessage="Cancel" />
                </Button>
              </ResponsiveModalClose>
              <Button
                onClick={onConfirm}
                variant="primary"
                hue="danger"
                disabled={isMutationInFlight}
                className="w-full"
              >
                <Trash01Icon className="w-5 h-5" />
                <FormattedMessage defaultMessage="Delete" />
              </Button>
            </div>
          </div>
        </ResponsiveModalContent>
      </ResponsiveModalPortal>
    </ResponsiveModal>
  );
}
